/**
 * OptimisedImage Component
 *
 * Wrapper around Next.js Image with intelligent handling of different image sources:
 *
 * 1. **Remote URLs (CMS/Vercel Blob)**: Uses Next.js built-in optimization
 *    - Vercel automatically converts to WebP/AVIF
 *    - Responsive srcsets generated at runtime
 *
 * 2. **Local images**: Uses pre-optimized WebP versions from /_optimized/
 *    - Generated by scripts/optimize-images.mjs
 *    - Fallback for local development without CMS
 *
 * Following CLAUDE.md:
 * - Uses Tailwind CSS utility classes
 * - Brand-agnostic (no brand logic)
 * - Uses cn() utility for conditional classes
 */

import Image, { type ImageProps } from "next/image";
import { cn } from "@/lib/utils";

export interface OptimisedImageProps extends Omit<ImageProps, "src"> {
	/**
	 * Image source - can be:
	 * - Remote URL (https://...) - passed directly to Next.js Image
	 * - Local path (/images/...) - converted to pre-optimized WebP
	 */
	src: string;
	/** Focal point X coordinate (0-100%), used for object-position */
	focalX?: number;
	/** Focal point Y coordinate (0-100%), used for object-position */
	focalY?: number;
}

/**
 * Converts local image paths to pre-optimized WebP versions
 * Remote URLs (http/https) are passed through unchanged for Next.js optimization
 */
function getOptimisedSrc(originalPath: string): string {
	// Remote URLs: let Next.js handle optimization (Vercel does WebP/AVIF conversion)
	if (
		originalPath.startsWith("http://") ||
		originalPath.startsWith("https://")
	) {
		return originalPath;
	}

	// Already optimized format: use directly
	if (/\.(webp|avif)$/i.test(originalPath)) {
		return originalPath;
	}

	// Local images: use pre-optimized WebP from /_optimized/ folder
	const lastSlash = originalPath.lastIndexOf("/");
	const dir = originalPath.substring(0, lastSlash);
	const filename = originalPath.substring(lastSlash + 1);
	const nameWithoutExt = filename.replace(/\.(jpg|jpeg|png)$/i, "");

	return `${dir}/_optimized/${nameWithoutExt}.webp`;
}

export function OptimisedImage({
	src,
	alt,
	className,
	focalX,
	focalY,
	style,
	...props
}: OptimisedImageProps) {
	const optimizedSrc = getOptimisedSrc(src);
	const hasFocalPoint = focalX != null && focalY != null;

	return (
		<Image
			src={optimizedSrc}
			alt={alt}
			className={cn(className)}
			style={
				hasFocalPoint
					? { ...style, objectPosition: `${focalX}% ${focalY}%` }
					: style
			}
			{...props}
		/>
	);
}
